import { Link } from 'react-router';
import type { TileSize } from '../../../tiles/registry';
import { Chip } from '../../../ui';
import { BigAmount } from '../components/MoneyCards';
import { spokenPeso } from '../../../ui/spokenPeso';
import { copy, leftText } from '../copy';
import { whatsLeftOf, type BudgetSnapshot, type FlowPeriod, type WhatsLeftConfig } from '../derive';
import { spokenDate } from '../format';
import { useBudgetToday, useFunds } from '../hooks';
import { FOCUS, LABEL } from '../styles';

/** "· ₱850 on your shopping list" for each planned-spending line (spec §6), shown, never subtracted. */
function plannedText(s: BudgetSnapshot): string {
  return s.planned
    .map((l) => ({ label: l.label, total: l.items.reduce((sum, i) => sum + i.amountMinor, 0) }))
    .filter((l) => l.total > 0)
    .map((l) => ` · ${copy.plannedLine(l.total, l.label)}`)
    .join('');
}

/** The hero tile (spec: wide, default on Today). Per-tile setting: which fund, per cutoff or per month. */
export function WhatsLeftTile({ size, config }: { size: TileSize; config: WhatsLeftConfig }) {
  const s = useBudgetToday();
  if (!s) return <p className="text-sm text-muted">{copy.loading}</p>;
  const v = whatsLeftOf(s, config ?? {});
  const href = v.fundId ? `/m/budget/funds/${v.fundId}` : '/m/budget/cashflow';
  return (
    <div
      role="group"
      aria-label={`${copy.whatsLeftSpoken}, ${spokenPeso(v.leftMinor)} until ${spokenDate(v.nextStart)}`}
      className="flex flex-col gap-1 [container-type:inline-size]"
    >
      <Link to={href} className={`inline-flex min-h-11 items-center ${LABEL} ${FOCUS}`}>
        {v.fundName ?? (config?.period === 'month' ? copy.thisMonth : copy.allIncome)}
      </Link>
      <BigAmount minor={v.leftMinor} maxPx={40} />
      <p className="text-sm text-muted">
        {leftText(v.leftMinor, v.nextStart)}
        {plannedText(s)}
      </p>
      {size === 'wide' ? (
        <p className="text-sm text-muted">
          {v.basis === 'fund'
            ? copy.spentOf(v.spentMinor, v.totalMinor)
            : copy.incomeAndSpent(v.totalMinor, v.spentMinor)}
          {v.reservedMinor > 0 ? ` · ${copy.afterPayments(v.reservedMinor)}` : ''}
        </p>
      ) : null}
    </div>
  );
}

export function WhatsLeftSettings({
  config,
  onChange,
}: {
  config: WhatsLeftConfig;
  onChange(c: WhatsLeftConfig): void;
}) {
  const funds = useFunds() ?? [];
  const source = config?.source ?? 'default';
  const period: FlowPeriod = config?.period ?? 'cutoff';
  const set = (patch: WhatsLeftConfig) => onChange({ ...config, ...patch });
  return (
    <div className="flex flex-col gap-3">
      <div role="group" aria-label={copy.whatsLeftSource} className="flex flex-wrap gap-2">
        <Chip
          selected={source === 'default'}
          aria-pressed={source === 'default'}
          onClick={() => set({ source: 'default' })}
        >
          {copy.defaultFund}
        </Chip>
        {funds.map((f) => (
          <Chip
            key={f.id}
            selected={source === f.id}
            aria-pressed={source === f.id}
            onClick={() => set({ source: f.id })}
          >
            {f.name}
          </Chip>
        ))}
        <Chip
          selected={source === 'income'}
          aria-pressed={source === 'income'}
          onClick={() => set({ source: 'income' })}
        >
          {copy.allIncome}
        </Chip>
      </div>
      <div role="group" aria-label={copy.period} className="flex gap-2">
        <Chip
          selected={period === 'cutoff'}
          aria-pressed={period === 'cutoff'}
          onClick={() => set({ period: 'cutoff' })}
        >
          {copy.perCutoff}
        </Chip>
        <Chip
          selected={period === 'month'}
          aria-pressed={period === 'month'}
          onClick={() => set({ period: 'month' })}
        >
          {copy.perMonth}
        </Chip>
      </div>
    </div>
  );
}
